import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { ListItemComponent } from '@frontend/sports/common/ui/sports-list-items';
import { CalendarUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { DeviceService, MenuActionsService } from '@frontend/vanilla/core';

import { IntervalCount } from '../../calendar/count-interval.service';
import { ItemComponent } from '../../common/item/item.component';
import { PopupManager } from '../../popup/popup-manager.service';
import { CountQuickLinksService } from './count-quick-links.service';

export interface CountQuickLink extends IntervalCount {
    url: string;
}

export interface CountQuickLinks {
    all?: CountQuickLink;
    live?: CountQuickLink;
    today?: CountQuickLink;
    home?: CountQuickLink;
}

@Component({
    imports: [DatePipe, AutomationModule, ItemComponent, ListItemComponent],
    selector: 'ms-count-quick-links',
    templateUrl: 'count-quick-links.html',
    styleUrls: ['count-quick-links.component.scss'],
})
export class CountQuickLinksComponent implements OnInit {
    @Input() homeLink: IntervalCount;
    links: CountQuickLinks = {};
    date = DateProviderService.getDate();
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private countQuicklinksService: CountQuickLinksService,
        private urlHelper: UrlHelperService,
        private popupManager: PopupManager,
        private sitecore: Sitecore,
        private menuActions: MenuActionsService,
        private deviceService: DeviceService,
        private changeRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.countQuicklinksService
            .getQuicklinkCounts()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((counts) => {
                counts.home = this.homeLink;

                if (this.deviceService.isMobile && counts.all) {
                    counts.all.title = this.sitecore.globalMessages.NavMenu;
                }

                this.links = {
                    live: this.getLink(counts.live, () => this.urlHelper.getLiveUrl()),
                    today: this.getLink(counts.today, () => this.urlHelper.getCalendarUrl(CalendarUrlParam.Today)),
                    all: this.getLink(counts.all, () => this.urlHelper.getPopupUrl('sports')),
                    home: this.getLink(counts.home, () => this.urlHelper.getHomeUrl()),
                };
                this.changeRef.markForCheck();
            });
    }

    private getLink(count: IntervalCount | undefined, getUrl: () => string): CountQuickLink | undefined {
        if (count) {
            return {
                ...count,
                url: getUrl(),
            };
        }

        return undefined;
    }

    openSportsPopup(): void {
        if (this.deviceService.isMobile) {
            this.menuActions.invoke('toggleProductMenu', '');
        } else {
            this.popupManager.open('sports');
        }
    }
}
